import AttachFileIcon from "@mui/icons-material/AttachFile";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Box,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { getDatabase, off, onValue, ref } from "firebase/database";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import AppLoader from "../../components/loader/loader";
import { receiveMessage, sendMessage } from "../chatBooking/SendMessage";

const style = {
  incoming: {
    p: "8px 10px",
    backgroundColor: "#F1F1F1",
    borderRadius: "12px",
    alignSelf: "flex-start",
    maxWidth: "70%",
    wordBreak: "break-word",
  },
  outgoing: {
    p: "8px 10px",
    backgroundColor: "#DCF8C6",
    borderRadius: "12px",
    alignSelf: "flex-end",
    maxWidth: "70%",
    wordBreak: "break-word",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "& .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `2px solid #F3F3F3`,
      },
    },
  },
};

function MessageBox() {
  const { selectedChat } = useSelector((state) => state.activeChat);
  const { user } = useSelector((state) => state.user);

  const [msgValue, setMsgValue] = useState("");
  const [allMessages, setAllMessages] = useState([]);
  const [imgUrl, setImgUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const messageContainerRef = useRef(null);
  const imageRef = useRef();

  useEffect(() => {
    if (!selectedChat) return;

    const dbRef = ref(
      getDatabase(),
      `messages/${user._id}/${selectedChat._id}`
    );
    const handleDataSnapshot = (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const tempArr = Object.keys(data).map((key) => ({
          sendBy: data[key]?.sender,
          ReceivedBy: data[key]?.receiver,
          msg: data[key]?.msg,
          time: data[key]?.time,
          attachmentUrl: data[key]?.attachmentUrl || null,
        }));

        setAllMessages(tempArr);

        setTimeout(() => {
          if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop =
              messageContainerRef.current.scrollHeight;
          }
        }, 100);
      } else {
        setAllMessages([]);
      }
    };

    onValue(dbRef, handleDataSnapshot);
    return () => off(dbRef, "value", handleDataSnapshot);
  }, [selectedChat]);

  const handleSend = async () => {
    if (!msgValue && !imgUrl) return;

    try {
      await sendMessage(msgValue, user._id, selectedChat._id, imgUrl);
      await receiveMessage(msgValue, user._id, selectedChat._id, imgUrl);
      setMsgValue("");
      setImgUrl(null);
    } catch (err) {
      alert("Failed to send message: " + err.message);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "tuytnefv");
    formData.append("cloud_name", "dgooan4ix");

    try {
      const res = await axios.post(
        "https://api.cloudinary.com/v1_1/dgooan4ix/upload",
        formData
      );
      setImgUrl(res.data.secure_url);
    } catch (err) {
      alert("Image upload failed: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2, borderRadius: "10px" }}>
      <AppLoader isloading={isLoading} />

      {selectedChat ? (
        <>
          <Stack direction="row" alignItems="center" gap={2}>
            <Avatar
              sx={{ borderRadius: "25%" }}
              src={selectedChat?.logoImage || selectedChat?.image}
              alt="User Avatar"
            />
            <Typography fontWeight="bold">
              {selectedChat.firstname} {selectedChat.lastname || ""}
            </Typography>
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Stack
            p={1}
            gap={1}
            height="51vh"
            overflow="auto"
            ref={messageContainerRef}
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "4px",
              },
            }}
          >
            {allMessages.map((msg, index) => (
              <Box
                key={index}
                sx={msg.sendBy !== user._id ? style.incoming : style.outgoing}
              >
                {msg.attachmentUrl && (
                  <img
                    src={msg.attachmentUrl}
                    alt="attachment"
                    style={{
                      width: "100%",
                      height: "250px",
                      objectFit: "contain",
                      borderRadius: "10px",
                      marginBottom: "5px",
                    }}
                  />
                )}
                <Typography variant="body2">{msg.msg}</Typography>
              </Box>
            ))}
          </Stack>

          {imgUrl && (
            <Box
              sx={{
                width: "150px",
                height: "150px",
                position: "relative",
                m: 1,
              }}
            >
              <Box
                onClick={() => setImgUrl(null)}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 1,
                  cursor: "pointer",
                }}
              >
                <CancelIcon />
              </Box>
              <img
                src={imgUrl}
                alt="preview"
                style={{ width: "100%", height: "100%", borderRadius: "10px" }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <TextField
              sx={style.searchField}
              fullWidth
              placeholder="Type a message"
              value={msgValue}
              onChange={(e) => setMsgValue(e.target.value)}
              onKeyUp={(e) => e.key === "Enter" && handleSend()}
              InputProps={{
                startAdornment: (
                  <Box mr={1}>
                    <AttachFileIcon
                      sx={{ transform: "rotate(45deg)", cursor: "pointer" }}
                      onClick={() => imageRef.current.click()}
                    />
                    <input
                      type="file"
                      hidden
                      ref={imageRef}
                      onChange={handleImageChange}
                      accept="image/*"
                    />
                  </Box>
                ),
                endAdornment: (
                  <Box ml={1} sx={{ cursor: "pointer" }} onClick={handleSend}>
                    <SendIcon />
                  </Box>
                ),
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="textSecondary">
            Select a user for conversation
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default MessageBox;
